import React from 'react';
import { NavLink } from 'react-router-dom';
import WPMLogo from './WPMLogo';

interface SidebarProps {
  isSuperuser?: boolean;
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
}

const navItems = (isSuperuser?: boolean) => [
  {
    to: '/',
    label: 'Sites',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-5 h-5 shrink-0">
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
        <rect x="14" y="14" width="7" height="7" rx="1" />
      </svg>
    ),
  },
  {
    to: '/site-users',
    label: 'Users',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-5 h-5 shrink-0">
        <circle cx="12" cy="8" r="4" />
        <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    to: '/site-machines',
    label: 'Machines',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-5 h-5 shrink-0">
        <rect x="2" y="4" width="20" height="14" rx="2" />
        <path d="M8 20h8M12 18v2" strokeLinecap="round" />
      </svg>
    ),
  },
  ...(isSuperuser
    ? [
        {
          to: '/admin-users',
          label: 'Admin Users',
          icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-5 h-5 shrink-0">
              <path d="M12 2l3 7h7l-5.5 4 2 7L12 16l-6.5 4 2-7L2 9h7z" strokeLinejoin="round" />
            </svg>
          ),
        },
      ]
    : []),
  {
    to: '/user-region-access',
    label: 'Region Access',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-5 h-5 shrink-0">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 3c-2.5 3-4 5.7-4 9s1.5 6 4 9" />
        <path d="M12 3c2.5 3 4 5.7 4 9s-1.5 6-4 9" />
        <path d="M3 12h18" />
      </svg>
    ),
  },
  {
    to: '/feedback',
    label: 'Feedback',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-5 h-5 shrink-0">
        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" strokeLinejoin="round" />
      </svg>
    ),
  },
];

const Sidebar: React.FC<SidebarProps> = ({ isSuperuser, collapsed, onCollapsedChange }) => {

  const items = navItems(isSuperuser);

  return (
    <aside
      className={`bg-slate-800 text-white shadow-[2px_0_5px_rgba(0,0,0,0.1)] flex flex-col transition-all duration-300 ease-in-out ${
        collapsed ? 'w-16' : 'w-56'
      }`}
    >
      {/* Header / toggle row */}
      <div className="p-3 border-b border-slate-700 flex items-center gap-3 h-[60px]">
        {!collapsed && (
          <>
            <WPMLogo size={32} />
            <div className="overflow-hidden">
              <div className="text-white font-bold text-base leading-tight tracking-wide whitespace-nowrap">WPM</div>
              <div className="text-slate-400 text-xs leading-tight whitespace-nowrap">Widget Portal Mgmt</div>
            </div>
          </>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); onCollapsedChange(!collapsed); }}
          className={`ml-auto p-1.5 rounded hover:bg-slate-700 text-slate-300 hover:text-white transition-colors shrink-0 ${
            collapsed ? 'mx-auto' : ''
          }`}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            /* hamburger → open */
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
              <path d="M4 6h16M4 12h16M4 18h16" strokeLinecap="round" />
            </svg>
          ) : (
            /* chevron left → close */
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
              <path d="M15 6l-6 6 6 6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 py-2">
        <ul className="list-none p-0 m-0">
          {items.map(({ to, label, icon }) => (
            <li key={to}>
              <NavLink
                to={to}
                end={to === '/'}
                className={({ isActive }) =>
                  `flex items-center gap-3 py-3 px-3.5 text-gray-200 no-underline transition-colors border-l-[3px] ${
                    isActive
                      ? 'bg-slate-700 border-l-blue-400 text-white'
                      : 'border-l-transparent hover:bg-slate-700 hover:text-white'
                  }`
                }
                title={collapsed ? label : undefined}
              >
                {icon}
                {!collapsed && (
                  <span className="text-sm font-medium whitespace-nowrap overflow-hidden">{label}</span>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </aside>
  );
};

export default Sidebar;
